import PlanChange from "../PlanChange";
import Model from "../Model";
import Person from "../Person";
import Income, {IncomeType} from "../Income";
import ModelStore from "../../stores/ModelStore";
import {CreateIncomeInput, UpdateIncomeInput} from "../../API";
import {
  createUUID, getDateToday,
  getISODateFromDate,
  getISODateTime,
  getISODateToday,
  isoToLocalDate
} from "../../stores/StoreUtilities";
import Schedule from "../Schedule";
import {addMonths, endOfMonth} from "date-fns";

export interface ISocialSecurityBenefit {
  personId: string
  fullBenefit: number
  personalStart?: string
  personalBenefit: number
  personalPercent?: number
  spousalStart?: string
  spousalBenefit: number
  spousalPercent?: number
  annualInf?: number
}

export interface ISocialSecurityStrategyChange {
  // Deprecated
  age1?: number
  startDate1?: string
  amount1?: number
  age2?: number
  startDate2?: string
  amount2?: number
  // New interface
  benefits?: ISocialSecurityBenefit[]
  applyReduction?: boolean
}

class SocialSecurityStrategyChange extends PlanChange implements ISocialSecurityStrategyChange {
  // Deprecated
  age1?: number
  startDate1?: string
  amount1?: number
  age2?: number
  startDate2?: string
  amount2?: number
  // New interface
  benefits?: ISocialSecurityBenefit[]
  applyReduction?: boolean

  constructor (data: any) {
    super(data)
    const items = this.details ? JSON.parse(this.details) : {}
    if (items.benefits) {
      this.benefits = items.benefits
    } else {
      this.benefits = []
      if (items.startDate1) {
        this.benefits.push({
          personId: "",
          fullBenefit: 0,
          personalStart: items.startDate1,
          personalBenefit: items.amount1,
          spousalStart: "",
          spousalBenefit: 0
        })
      }
      if (items.startDate2) {
        this.benefits.push({
          personId: "",
          fullBenefit: 0,
          personalStart: items.startDate2,
          personalBenefit: items.amount2,
          spousalStart: "",
          spousalBenefit: 0
        })
      }
    }
    // this.age1 = items.age1
    // this.startDate1 = items.startDate1
    // this.amount1 = items.amount1
    // this.age2 = items.age2
    // this.startDate2 = items.startDate2
    // this.amount2 = items.amount2
    this.applyReduction = items.applyReduction
  }

  static reductionSchedule = new Map<number, number>([[2034, -0.19]])

  tempCreatedAt = "2199-12-31"

  apply(model: Model) {
    if (!this.benefits) {
      return
    }
    /*  incomes   personalBenefit   spousalBenefit
           0              -1               -1
           1               0               -1
           1              -1                0
           2               0                1
     */
    this.benefits.forEach((benefit: ISocialSecurityBenefit, index: number) => {
      const person = (index === 0) ? model.person1 : model.person2
      if (person) {
        let personalIndex = -1
        let spousalIndex = -1
        const incomes = this.getSocialSecurityIncomes(model, person)
        if (incomes.length === 1) {
          if ((benefit.personalStart && benefit.personalBenefit) || !benefit.spousalStart) {
            personalIndex = 0
          } else if (benefit.spousalStart && benefit.spousalBenefit) {
            spousalIndex = 0
          }
        } else if (incomes.length > 1) {
          personalIndex = 0
          spousalIndex = 1
        }

        if (personalIndex >= 0) {
          const income = incomes[personalIndex]
          income.description = `${person.nickname} Social Security Benefit`
          income.start = benefit.personalStart ?? ""
          income.amount = benefit.personalBenefit ?? 0
          income.amountDate = getDateToday()
          income.end = benefit.spousalStart ? this.priorMonth(benefit.spousalStart) : ""
          income.annualInf = benefit.annualInf ?? 0
          income.annualInfSchedule = this.applyReduction ? SocialSecurityStrategyChange.reductionSchedule : undefined
          model.addLock(income.id, "*")
        } else if (benefit.personalStart && benefit.personalBenefit) {
          const income1 = new Income({
            id: createUUID(),
            createdAt: this.tempCreatedAt,
            updatedAt: getISODateTime(),
            accountId: model.accountId,
            userId: model.userId,
            modelId: model.id,
            model: model,
            description: `${person.nickname} Social Security Benefit`,
            amount: benefit.personalBenefit,
            amountDate: getISODateToday(),
            incomeType: IncomeType.SocialSecurityBenefits,
            schedule: Schedule.getDefaultSchedule("Monthly"),
            start: benefit.personalStart,
            end: (benefit.spousalStart) ? this.priorMonth(benefit.spousalStart) : null,
            annualInf: benefit.annualInf ?? 0,
            annualInfSchedule: this.applyReduction ? SocialSecurityStrategyChange.reductionSchedule : null,
            infLock: false,
            survivorPercent: null,
            ownerId: person.id,
            owner: person,
            sortOrder: model.incomes.length > 0 ? model.incomes[model.incomes.length-1].sortOrder + ModelStore.orderInterval : 0
          })
          model.incomes.push(income1)
          model.addLock(income1.id, "*")
        }

        if (spousalIndex >= 0) {
          const income = incomes[spousalIndex]
          income.description = `${person.nickname} Spousal Social Security Benefit`
          income.start = benefit.spousalStart ?? ""
          income.amount = benefit.spousalBenefit ?? 0
          income.amountDate = getDateToday()
          income.end = ""
          income.annualInf = benefit.annualInf ?? 0
          income.annualInfSchedule = this.applyReduction ? SocialSecurityStrategyChange.reductionSchedule : undefined
          model.addLock(income.id, "*")
        } else if (benefit.spousalStart && benefit.spousalBenefit) {
          // Add a spousal benefit Income
          const income2 = new Income({
            id: createUUID(),
            createdAt: this.tempCreatedAt,
            updatedAt: getISODateTime(),
            accountId: model.accountId,
            userId: model.userId,
            modelId: model.id,
            model: model,
            description: `${person.nickname} Spousal Social Security Benefit`,
            amount: benefit.spousalBenefit,
            amountDate: getISODateToday(),
            incomeType: IncomeType.SocialSecurityBenefits,
            schedule: Schedule.getDefaultSchedule("Monthly"),
            start: benefit.spousalStart,
            end: null,
            annualInf: benefit.annualInf ?? 0,
            annualInfSchedule: this.applyReduction ? SocialSecurityStrategyChange.reductionSchedule : null,
            infLock: false,
            survivorPercent: null,
            ownerId: person.id,
            owner: person,
            sortOrder: model.incomes.length > 0 ? model.incomes[model.incomes.length-1].sortOrder + ModelStore.orderInterval : 0
          })
          model.incomes.push(income2)
          model.addLock(income2.id, "*")
        }

        if (incomes.length > 2) {
          // Disable additional social security incomes
          for (let i = 2; i < incomes.length; i++) {
            const income = incomes[i]
            income.start = ""
            income.amount = 0
            income.amountDate = getDateToday()
            income.end = ""
            income.annualInf = 0
            model.addLock(income.id, "*")
          }
        }
      }
    })

    Model.sortIncomes(model.incomes)
  }

  getSocialSecurityIncomes(model: Model, person: Person): Income[] {
    const incomes = model.incomes.filter((i: Income) => i.incomeType === IncomeType.SocialSecurityBenefits && i.ownerId === person.id && !i.archived)
    return incomes
  }

  priorMonth(isoDate: string) {
    const date = endOfMonth(addMonths(isoToLocalDate(isoDate), -1))
    return getISODateFromDate(date)
  }

  async commit(model: Model, modelStore: ModelStore) {
    if (!this.benefits) {
      return
    }

    let index = 0
    for (const benefit of this.benefits) {
      const person = (index === 0) ? model.person1 : model.person2
      if (person) {
        let personalIndex = -1
        let spousalIndex = -1
        const incomesTemp = this.getSocialSecurityIncomes(model, person)
        const incomes: Income[] = []
        // clear out temporary incomes
        for (let income of incomesTemp) {
          if (income.createdAt === this.tempCreatedAt) {
            const i = model.incomes.findIndex((inc: Income) => inc.id === income.id)
            if (i >= 0) {
              model.incomes.splice(i, 1)
            }
          } else {
            incomes.push(income)
          }
        }

        if (incomes.length === 1) {
          if ((benefit.personalStart && benefit.personalBenefit) || !benefit.spousalStart) {
            personalIndex = 0
          } else if (benefit.spousalStart && benefit.spousalBenefit) {
            spousalIndex = 0
          }
        } else if (incomes.length > 1) {
          personalIndex = 0
          spousalIndex = 1
        }

        if (personalIndex >= 0) {
          const income = incomes[personalIndex]
          if (benefit.personalStart && benefit.personalBenefit) {
            const update: UpdateIncomeInput = {
              id: income.id,
              accountId: income.accountId,
              description: `${person.nickname} Social Security Benefit`,
              amount: benefit.personalBenefit,
              amountDate: getISODateToday(),
              start: benefit.personalStart,
              end: benefit.spousalStart ? this.priorMonth(benefit.spousalStart) : null,
              annualInf: benefit.annualInf ?? 0
            }
            await modelStore.updateIncome(update)
          } else {
            // Delete it
            await modelStore.deleteIncome(income.id)
          }
          model.deleteLock(income.id, "*")
        } else if (benefit.personalStart && benefit.personalBenefit > 0) {
          // Create social security income
          const input: CreateIncomeInput = {
            accountId: model.accountId,
            userId: model.userId,
            modelId: model.id,
            description: `${person.nickname} Social Security Benefit`,
            amount: benefit.personalBenefit,
            amountDate: getISODateToday(),
            incomeType: IncomeType.SocialSecurityBenefits,
            schedule: JSON.stringify(Schedule.getDefaultSchedule("Monthly")),
            start: benefit.personalStart,
            end: (benefit.spousalStart) ? this.priorMonth(benefit.spousalStart) : null,
            annualInf: benefit.annualInf ?? 0,
            infLock: false,
            survivorPercent: null,
            ownerId: person.id,
            sortOrder: model.incomes.length > 0 ? model.incomes[model.incomes.length-1].sortOrder + ModelStore.orderInterval : 0
          }
          const income1 = await modelStore.createIncome(input)
        }

        if (spousalIndex >= 0) {
          const income = incomes[spousalIndex]
          if (benefit.spousalStart && benefit.spousalBenefit) {
            const update: UpdateIncomeInput = {
              id: income.id,
              accountId: income.accountId,
              description: `${person.nickname} Spousal Social Security Benefit`,
              amount: benefit.spousalBenefit,
              amountDate: getISODateToday(),
              start: benefit.spousalStart,
              end: null,
              annualInf: benefit.annualInf ?? 0
            }
            await modelStore.updateIncome(update)
          } else {
            // Delete it
            await modelStore.deleteIncome(income.id)
          }
          model.deleteLock(income.id, "*")
        } else if (benefit.spousalStart && benefit.spousalBenefit) {
          // Add a spousal benefit Income
          const input: CreateIncomeInput = {
            accountId: model.accountId,
            userId: model.userId,
            modelId: model.id,
            description: `${person.nickname} Spousal Social Security Benefit`,
            amount: benefit.spousalBenefit,
            amountDate: getISODateToday(),
            incomeType: IncomeType.SocialSecurityBenefits,
            schedule: JSON.stringify(Schedule.getDefaultSchedule("Monthly")),
            start: benefit.spousalStart,
            end: null,
            annualInf: benefit.annualInf ?? 0,
            infLock: false,
            survivorPercent: null,
            ownerId: person.id,
            sortOrder: model.incomes.length > 0 ? model.incomes[model.incomes.length - 1].sortOrder + ModelStore.orderInterval : 0
          }
          const income2 = await modelStore.createIncome(input)
        }

        if (incomes.length > 2) {
          // Delete additional social security incomes
          for (let i = 2; i < incomes.length; i++) {
            await modelStore.deleteIncome(incomes[i].id)
          }
        }
      }
      ++index
    }
    Model.sortIncomes(model.incomes)
  }
}

export default SocialSecurityStrategyChange